import React, { useState, useEffect } from 'react';
import './QuizQuestionLinker.css';
import API_BASE_URL from './config';
import CustomSelect from '../components/CustomSelect';

const QuizQuestionLinker = ({ selectedQuiz, onQuestionsAdded }) => {
    const [questions, setQuestions] = useState([]);
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [questionsInQuiz, setQuestionsInQuiz] = useState([]);
    
    // Filter states
    const [filters, setFilters] = useState({
        questionType: 'all',
        difficulty: 'all',
        dateRange: 'all',
        searchText: '',
        topic: '',
        subtopic: ''
    });
    
    // Dropdown options states
    const [topics, setTopics] = useState([]);
    const [subtopics, setSubtopics] = useState([]);
    const [showTopicFilter, setShowTopicFilter] = useState(false);
    const [showSubtopicFilter, setShowSubtopicFilter] = useState(false);
    
    // Preview modal state
    const [previewQuestion, setPreviewQuestion] = useState(null);

    useEffect(() => {
        if (selectedQuiz) {
            loadQuestions();
            checkFilterVisibility();
        }
    }, [selectedQuiz]);

    useEffect(() => {
        applyFilters();
    }, [filters, questions]);

    // Reload subtopics when topic filter changes
    useEffect(() => {
        if (showSubtopicFilter && filters.topic && filters.topic !== '') {
            // Load subtopics for the selected topic
            loadSubtopicsForTopic(parseInt(filters.topic));
        } else if (showSubtopicFilter && (!filters.topic || filters.topic === '')) {
            // If no topic selected, load all subtopics for the chapter
            if (selectedQuiz) {
                loadSubtopicsForChapter(selectedQuiz.chapter_id);
            }
        }
    }, [filters.topic]);

    const checkFilterVisibility = async () => {
        try {
            // Check if topic is "ALL TOPICS"
            const topicResponse = await fetch(`${API_BASE_URL}/topics/?topic_id=${selectedQuiz.topic_id}`);
            const topicData = await topicResponse.json();
            const topic = topicData.find(t => t.topic_id === selectedQuiz.topic_id);
            
            if (topic && topic.name.toUpperCase() === "ALL TOPICS") {
                setShowTopicFilter(true);
                loadTopicsForChapter(selectedQuiz.chapter_id);
            } else {
                setShowTopicFilter(false);
            }

            // Check if subtopic is "ALL SUBTOPICS"
            const subtopicResponse = await fetch(`${API_BASE_URL}/subtopics/?subtopic_id=${selectedQuiz.subtopic_id}`);
            const subtopicData = await subtopicResponse.json();
            const subtopic = subtopicData.find(st => st.subtopic_id === selectedQuiz.subtopic_id);
            
            if (subtopic && subtopic.name.toUpperCase() === "ALL SUBTOPICS") {
                setShowSubtopicFilter(true);
                // Load subtopics based on topic selection
                if (topic && topic.name.toUpperCase() === "ALL TOPICS") {
                    // Load all subtopics for the chapter
                    loadSubtopicsForChapter(selectedQuiz.chapter_id);
                } else {
                    // Load subtopics for the specific topic
                    loadSubtopicsForTopic(selectedQuiz.topic_id);
                }
            } else {
                setShowSubtopicFilter(false);
            }
        } catch (err) {
            console.error('Error checking filter visibility:', err);
        }
    };

    const loadTopicsForChapter = async (chapterId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/topics/?chapter_id=${chapterId}`);
            const data = await response.json();
            setTopics(data.filter(t => t.name.toUpperCase() !== "ALL TOPICS"));
        } catch (err) {
            console.error('Error loading topics:', err);
        }
    };

    const loadSubtopicsForChapter = async (chapterId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/subtopics/?chapter_id=${chapterId}`);
            const data = await response.json();
            setSubtopics(data.filter(st => st.name.toUpperCase() !== "ALL SUBTOPICS"));
        } catch (err) {
            console.error('Error loading subtopics:', err);
        }
    };

    const loadSubtopicsForTopic = async (topicId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/subtopics/?topic_id=${topicId}`);
            const data = await response.json();
            setSubtopics(data.filter(st => st.name.toUpperCase() !== "ALL SUBTOPICS"));
        } catch (err) {
            console.error('Error loading subtopics:', err);
        }
    };

    const loadAllSubtopicsForChapter = async (chapterId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/subtopics/?chapter_id=${chapterId}`);
            const data = await response.json();
            setSubtopics(data.filter(st => st.name.toUpperCase() !== "ALL SUBTOPICS"));
        } catch (err) {
            console.error('Error loading subtopics for chapter:', err);
        }
    };

    const loadQuestions = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const params = new URLSearchParams({
                subject_id: selectedQuiz.subject_id,
                chapter_id: selectedQuiz.chapter_id,
                topic_id: selectedQuiz.topic_id
            });
            
            if (selectedQuiz.subtopic_id) {
                params.append('subtopic_id', selectedQuiz.subtopic_id);
            }

            const response = await fetch(`${API_BASE_URL}/quiz/question/?${params}`);
            const data = await response.json();
            setQuestions(data);
            setFilteredQuestions(data);

            // Load questions already in this quiz
            const quizQuestionsResponse = await fetch(
                `${API_BASE_URL}/quiz/quiz/${selectedQuiz.quiz_id}/questions`
            );
            const quizQuestionsData = await quizQuestionsResponse.json();
            const existingQuestionIds = quizQuestionsData.map(q => q.quiz_question_id);
            setQuestionsInQuiz(existingQuestionIds);


        } catch (err) {
            setError('Failed to load questions');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...questions];

        
        // Apply question type filter
        if (filters.questionType && filters.questionType !== 'all') {
            filtered = filtered.filter(q => 
                q.question_type && q.question_type.toLowerCase() === filters.questionType.toLowerCase()
            );
        
        }

        // Apply difficulty filter
        if (filters.difficulty && filters.difficulty !== 'all') {
            filtered = filtered.filter(q => q.difficulty_level === filters.difficulty);
        
        }

        // Apply topic filter - ONLY if topic filter dropdown is visible
        if (showTopicFilter && filters.topic && filters.topic !== '') {
        
            filtered = filtered.filter(q => {
                        return q.topic_id === parseInt(filters.topic);
            });
        
        }

        // Apply subtopic filter - ONLY if subtopic filter dropdown is visible
        if (showSubtopicFilter && filters.subtopic && filters.subtopic !== '') {
            filtered = filtered.filter(q => q.subtopic_id === parseInt(filters.subtopic));
           
        }

        // Apply search text filter
        if (filters.searchText && filters.searchText.trim() !== '') {
            const searchLower = filters.searchText.toLowerCase();
            filtered = filtered.filter(q => 
                q.question_text.toLowerCase().includes(searchLower)
            );
        }

        // Apply date range filter
        if (filters.dateRange && filters.dateRange !== 'all') {
            const now = new Date();
            let filterDateFrom = null;
            let filterDateTo = null;
            
            switch (filters.dateRange) {
                case 'last7days':
                    filterDateFrom = new Date();
                    filterDateFrom.setDate(now.getDate() - 7);
                    break;
                case 'last30days':
                    filterDateFrom = new Date();
                    filterDateFrom.setDate(now.getDate() - 30);
                    break;
                case 'last3months':
                    filterDateFrom = new Date();
                    filterDateFrom.setMonth(now.getMonth() - 3);
                    break;
                case 'custom':
                    if (filters.customDateFrom) {
                        filterDateFrom = new Date(filters.customDateFrom);
                    }
                    if (filters.customDateTo) {
                        filterDateTo = new Date(filters.customDateTo);
                        filterDateTo.setHours(23, 59, 59, 999); // Include entire end date
                    }
                    break;
                default:
                    break;
            }
            
            if (filterDateFrom || filterDateTo) {
                filtered = filtered.filter(q => {
                    const questionDate = new Date(q.created_at);
                    if (filterDateFrom && filterDateTo) {
                        return questionDate >= filterDateFrom && questionDate <= filterDateTo;
                    } else if (filterDateFrom) {
                        return questionDate >= filterDateFrom;
                    } else if (filterDateTo) {
                        return questionDate <= filterDateTo;
                    }
                    return true;
                });
            }
        }

        
        setFilteredQuestions(filtered);
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    const handleSelectAll = () => {
        if (selectedQuestionIds.length === filteredQuestions.length) {
            setSelectedQuestionIds([]);
        } else {
            setSelectedQuestionIds(filteredQuestions.map(q => q.quiz_question_id));
        }
    };

    const handleQuestionSelect = (questionId) => {
        setSelectedQuestionIds(prev => {
            if (prev.includes(questionId)) {
                return prev.filter(id => id !== questionId);
            } else {
                return [...prev, questionId];
            }
        });
    };

    const handleAddSelected = async () => {
        if (selectedQuestionIds.length === 0) {
            setError('Please select at least one question');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let successCount = 0;
            let failCount = 0;

            for (let i = 0; i < selectedQuestionIds.length; i++) {
                const questionId = selectedQuestionIds[i];
                
                // Get current question count for ordering
                const currentQuestionsResponse = await fetch(
                    `${API_BASE_URL}/quiz/quiz/${selectedQuiz.quiz_id}/questions`
                );
                const questionsData = await currentQuestionsResponse.json();
                const nextOrder = questionsData.length + 1;

                const response = await fetch(
                    `${API_BASE_URL}/quiz/quiz/${selectedQuiz.quiz_id}/add-question/${questionId}?question_order=${nextOrder}`,
                    { method: 'POST' }
                );

                if (response.ok) {
                    successCount++;
                } else {
                    failCount++;
                }
            }

            setSuccess(`Successfully added ${successCount} question(s) to quiz!`);
            setSelectedQuestionIds([]);
            
            if (onQuestionsAdded) {
                onQuestionsAdded();
            }

            // Reload questions to update the list
            loadQuestions();
        } catch (err) {
            setError('Failed to add questions');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB');
    };

    return (
        <div className="question-linker">
            {error && (
                <div className="alert alert-error">
                    {error}
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            {success && (
                <div className="alert alert-success">
                    {success}
                    <button onClick={() => setSuccess(null)}>×</button>
                </div>
            )}

            <div className="linker-header">
                <h3>Link Questions to Quiz: {selectedQuiz.quiz_title}</h3>
                <p>Showing {filteredQuestions.length} of {questions.length} questions</p>
            </div>

            {/* Compact Filters */}
            <div className="question-filters">
                {showTopicFilter && (
                    <CustomSelect
                        value={filters.topic}
                        onChange={(e) => {
                            handleFilterChange('topic', e.target.value);
                            handleFilterChange('subtopic', '');
                        }}
                        className="filter-select"
                        placeholder="All Topics"
                        options={[
                            { value: '', label: 'All Topics' },
                            ...topics.map(t => ({ value: t.topic_id, label: t.name }))
                        ]}
                    />
                )}

                {showSubtopicFilter && (
                    <CustomSelect
                        value={filters.subtopic}
                        onChange={(e) => handleFilterChange('subtopic', e.target.value)}
                        className="filter-select"
                        placeholder="All Subtopics"
                        options={[
                            { value: '', label: 'All Subtopics' },
                            ...subtopics.map(s => ({ value: s.subtopic_id, label: s.name }))
                        ]}
                    />
                )}

                <CustomSelect
                    value={filters.questionType}
                    onChange={(e) => handleFilterChange('questionType', e.target.value)}
                    className="filter-select"
                    options={[
                        { value: 'all', label: 'All Types' },
                        { value: 'analytical', label: 'Analytical' },
                        { value: 'concept', label: 'Concept' },
                        { value: 'calculation', label: 'Calculation' },
                        { value: 'competency', label: 'Competency' },
                    ]}
                />

                <CustomSelect
                    value={filters.difficulty}
                    onChange={(e) => handleFilterChange('difficulty', e.target.value)}
                    className="filter-select"
                    options={[
                        { value: 'all', label: 'All Difficulties' },
                        { value: 'easy', label: 'Easy' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'hard', label: 'Hard' },
                    ]}
                />

                <CustomSelect
                    value={filters.dateRange}
                    onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                    className="filter-select"
                    options={[
                        { value: 'all', label: 'All Time' },
                        { value: 'last7days', label: 'Last 7 Days' },
                        { value: 'last30days', label: 'Last 30 Days' },
                        { value: 'last3months', label: 'Last 3 Months' },
                        { value: 'custom', label: 'Custom Date Range' },
                    ]}
                />

                {filters.dateRange === 'custom' && (
                    <>
                        <input
                            type="date"
                            value={filters.customDateFrom}
                            onChange={(e) => handleFilterChange('customDateFrom', e.target.value)}
                            className="filter-date"
                            placeholder="From"
                        />
                        <input
                            type="date"
                            value={filters.customDateTo}
                            onChange={(e) => handleFilterChange('customDateTo', e.target.value)}
                            className="filter-date"
                            placeholder="To"
                        />
                    </>
                )}

                <input
                    type="text"
                    placeholder="Search questions..."
                    value={filters.searchText}
                    onChange={(e) => handleFilterChange('searchText', e.target.value)}
                    className="filter-search"
                />
            </div>

            {/* Bulk Actions */}
            <div className="bulk-actions">
                <label className="select-all">
                    <input
                        type="checkbox"
                        checked={selectedQuestionIds.length === filteredQuestions.length && filteredQuestions.length > 0}
                        onChange={handleSelectAll}
                    />
                    Select All ({filteredQuestions.length})
                </label>

                <button 
                    className="add-selected-btn"
                    onClick={handleAddSelected}
                    disabled={selectedQuestionIds.length === 0 || loading}
                >
                    {loading ? 'Adding...' : `Add Selected (${selectedQuestionIds.length}) to Quiz`}
                </button>
            </div>

            {/* Questions Table */}
            {loading ? (
                <div className="loading">Loading questions...</div>
            ) : filteredQuestions.length === 0 ? (
                <div className="no-questions">No questions found matching your filters</div>
            ) : (
                <div className="questions-table-container">
                    <table className="questions-table">
                        <thead>
                            <tr>
                                <th className="col-checkbox"></th>
                                <th className="col-id">ID</th>
                                <th className="col-question">Question</th>
                                <th className="col-date">Upload Date</th>
                                <th className="col-preview"></th>
                            </tr>
                        </thead>
                            <tbody>
                            {filteredQuestions.map(question => {
                                const alreadyInQuiz = questionsInQuiz.includes(question.quiz_question_id);
                                
                                return (
                                    <tr key={question.quiz_question_id} className={alreadyInQuiz ? 'already-added' : ''}>
                                        <td className="col-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={selectedQuestionIds.includes(question.quiz_question_id)}
                                                onChange={() => handleQuestionSelect(question.quiz_question_id)}
                                                disabled={alreadyInQuiz}
                                            />
                                        </td>
                                        <td className="col-id">{question.quiz_question_id}</td>
                                        <td className="col-question">
                                            {alreadyInQuiz && <span className="already-added-badge">✓ Added</span>}
                                            {question.question_text.length > 100
                                                ? question.question_text.substring(0, 100) + '...'
                                                : question.question_text
                                            }
                                        </td>
                                        <td className="col-date">{formatDate(question.created_at)}</td>
                                        <td className="col-preview">
                                            <button 
                                                className="preview-btn"
                                                onClick={() => setPreviewQuestion(question)}
                                                title="Preview question"
                                            >
                                                👁️
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Preview Modal */}
            {previewQuestion && (
                <div className="modal-overlay" onClick={() => setPreviewQuestion(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Question Preview #{previewQuestion.quiz_question_id}</h3>
                            <button className="modal-close" onClick={() => setPreviewQuestion(null)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="preview-section">
                                <strong>Question:</strong>
                                <p>{previewQuestion.question_text}</p>
                            </div>
                            
                            <div className="preview-section">
                                <strong>Options:</strong>
                                <div className="preview-options">
                                    <p><strong>A)</strong> {previewQuestion.optionA}</p>
                                    <p><strong>B)</strong> {previewQuestion.optionB}</p>
                                    {previewQuestion.optionC && <p><strong>C)</strong> {previewQuestion.optionC}</p>}
                                    {previewQuestion.optionD && <p><strong>D)</strong> {previewQuestion.optionD}</p>}
                                </div>
                            </div>

                            <div className="preview-section">
                                <strong>Correct Answer:</strong>
                                <p className="correct-answer">{previewQuestion.correct_answer}</p>
                            </div>

                            {previewQuestion.explanation && (
                                <div className="preview-section">
                                    <strong>Explanation:</strong>
                                    <p>{previewQuestion.explanation}</p>
                                </div>
                            )}

                            <div className="preview-meta">
                                <span className="meta-badge">{previewQuestion.question_type}</span>
                                <span className="meta-badge">{previewQuestion.difficulty_level}</span>
                                <span className="meta-badge">Uploaded: {formatDate(previewQuestion.created_at)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuizQuestionLinker;